"use client";

import * as Sentry from "@sentry/nextjs";
import { Component, ErrorInfo, ReactNode } from "react";
import Image from "next/image";
import { RefreshCcw, Terminal, ChevronDown } from "lucide-react";
import { Button } from "@/components/button";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div
          className="fixed inset-0 z-9999 flex flex-col items-center justify-start overflow-y-auto bg-white/90 p-4 backdrop-blur-xl md:justify-center"
          dir="rtl"
        >
          {/* کانتینر اصلی - تغییر از flex به grid در دسکتاپ برای کنترل بهتر */}
          <div className="relative my-4 flex w-full max-w-7xl flex-col items-center overflow-hidden rounded-4xl border border-slate-100 bg-white shadow-2xl md:my-0 md:grid md:grid-cols-2 md:items-stretch md:rounded-[3rem]">
            {/* بخش محتوا و نمایش خطا */}
            <div className="flex w-full flex-col justify-center p-6 text-center md:p-6 md:text-right">
              <div className="mb-6 inline-flex self-center items-center gap-2 rounded-full bg-primary/5 px-5 py-2 text-sm font-bold text-primary md:self-start">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-primary"></span>
                </span>
                <span>همراه شما هستیم</span>
              </div>

              <h2 className="mb-6 text-4xl font-black leading-tight text-slate-900 md:text-6xl">
                در حال بررسی <br className="hidden md:block" /> سیستم هستیم...
              </h2>

              <p className="mb-10 max-w-lg text-xl leading-relaxed text-slate-500">
                به نظر می‌رسد سیستم برای لحظاتی نیاز به بررسی دارد. لطفاً لحظاتی
                دیگر تلاش کنید .
              </p>

              <div className="flex w-full flex-col gap-4 sm:flex-row justify-center md:justify-start">
                <Button
                  variant="bare"
                  type="button"
                  text="تلاش مجدد"
                  onClick={this.handleRetry}
                  iconNode={
                    <RefreshCcw className="h-5 w-5 transition-transform group-hover:rotate-180" />
                  }
                  className="group inline-flex h-14 items-center justify-center gap-3 rounded-2xl bg-primary px-10 text-lg font-bold text-white transition-all hover:bg-[#3730d8] hover:shadow-xl hover:shadow-primary/30 active:scale-95"
                />
              </div>

              {/* کادر جزئیات خطا - کاملاً ریسپانسیو با اسکرول داخلی */}
              {process.env.NODE_ENV === "development" && (
                <div className="mt-10 w-full overflow-hidden text-right">
                  <details className="group rounded-2xl border border-slate-200 bg-slate-50 transition-all">
                    <summary className="flex cursor-pointer items-center justify-between p-4 text-slate-600 outline-none group-open:bg-slate-100">
                      <div className="flex items-center gap-2 font-bold text-sm">
                        <Terminal className="h-4 w-4 text-primary" />
                        <span>جزئیات فنی خطا (Debug)</span>
                      </div>
                      <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
                    </summary>

                    <div className="max-h-50 overflow-x-auto overflow-y-auto bg-white p-4 shadow-inner">
                      <div className="mb-2 text-[10px] font-bold text-red-500 ltr-dir text-left">
                        {this.state.error?.name}: {this.state.error?.message}
                      </div>
                      <pre className="text-[10px] leading-normal text-slate-500 ltr-dir font-mono">
                        {this.state.errorInfo?.componentStack}
                      </pre>
                    </div>
                  </details>
                </div>
              )}
            </div>
            {/* بخش تصویر - در موبایل بالا، در دسکتاپ چپ */}
            <div className="relative flex w-full items-center justify-center bg-slate-50/50 p-8 md:p-12">
              <div className="relative h-62.5 w-full sm:h-87.5 md:h-full max-h-125">
                <Image
                  src="/assets/errorBoundry.png"
                  alt="Real Estate System Error"
                  fill
                  sizes="(max-width: 768px) 100vw, 50vw"
                  className="object-contain"
                  priority
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
